// El ranking por puntos, al estilo ATP / Premier Padel.
//
// Cada mes cerrado es un "torneo" que reparte puntos segun como termino cada
// uno en la tabla mensual. El ranking es la suma de los ultimos 12 meses
// cerrados: cuando cierra septiembre de 2027 se caen los de septiembre de 2026
// ("defender los puntos").
//
// Reglas elegidas por Marcos el 2026-09-27:
//   - La escala: 1000, 600, 360, 250, 150 (5º a 8º), 80 (9º a 12º), 40 el resto.
//   - Los puestos se reparten SOLO entre los elegibles del mes (los que llegaron
//     al minimo de partidos, la misma regla que la corona). Un no elegible que
//     jugo algo se lleva el minimo: nadie que se presento queda en cero, pero
//     venir dos veces y ganar las dos no le saca el puesto al que vino cinco.
//   - Empatados en puntos del mes se reparten el promedio de los puestos que
//     ocupan (Meyi y Pico con 18: (600 + 360) / 2 = 480 cada uno). Los
//     desempates de la tabla mensual ordenan la pantalla, no los puntos.
//
// Es todo derivado y se calcula al vuelo desde `ranking_mensual`: no hay nada
// guardado que se pueda desincronizar.

import { sumarMeses } from './fechas';

/**
 * Si el grupo ve la solapa Ranking. Apagado el 2026-09-27 para llevarlo a
 * votacion; el 2026-09-30 **no paso la votacion** ("por ahora es demasiado
 * complicado") y quedo deshabilitado hasta nuevo aviso. La pregunta frecuente
 * tampoco sale. La cuenta y sus pruebas quedan: publicarlo es poner esto en true.
 */
export const RANKING_PUBLICO = false;

/**
 * Si el admin la ve aunque el grupo no (con un cartel), para mostrarla. Servia
 * durante la votacion; deshabilitado, tampoco la ve el admin. Con los dos en
 * false la solapa no aparece y GET /api/ranking/puntos contesta 404.
 */
export const RANKING_PARA_ADMIN = false;

/** Si este jugador ve el Ranking: lo usan la pantalla y el Worker, asi dicen lo mismo. */
export function veRanking(rol: 'jugador' | 'admin'): boolean {
  return RANKING_PUBLICO || (RANKING_PARA_ADMIN && rol === 'admin');
}

/** Puntos por puesto: el indice 0 es el 1º. Del 13º en adelante, PUNTOS_RESTO. */
export const ESCALA = [1000, 600, 360, 250, 150, 150, 150, 150, 80, 80, 80, 80] as const;
/** Lo que se lleva el elegible del 13º para abajo, y cualquiera que jugo sin llegar al minimo. */
export const PUNTOS_RESTO = 40;
/** Cuantos meses cerrados suman, como las 52 semanas del ATP. */
export const MESES_QUE_CUENTAN = 12;

export interface LineaDelMes {
  jugador: number;
  /** Puntos de la tabla mensual (3 por ganado, 1 por jugado). */
  puntos: number;
  partidos: number;
  elegible: boolean;
}

function puntosDelPuesto(puesto: number): number {
  return ESCALA[puesto - 1] ?? PUNTOS_RESTO;
}

/** Lo que reparte un mes: jugador -> puntos del ranking. */
export function puntosDelMes(tabla: readonly LineaDelMes[]): Map<number, number> {
  const reparto = new Map<number, number>();

  const elegibles = tabla.filter((l) => l.elegible).sort((a, b) => b.puntos - a.puntos);
  let i = 0;
  while (i < elegibles.length) {
    // El grupo de empatados en puntos ocupa los puestos i+1 .. i+n.
    let n = 1;
    while (i + n < elegibles.length && elegibles[i + n]!.puntos === elegibles[i]!.puntos) n++;
    let suma = 0;
    for (let k = 1; k <= n; k++) suma += puntosDelPuesto(i + k);
    // Redondeado: un triple empate daria decimales, y el ranking se lee en enteros.
    const cada = Math.round(suma / n);
    for (let k = 0; k < n; k++) reparto.set(elegibles[i + k]!.jugador, cada);
    i += n;
  }

  for (const l of tabla) {
    if (!l.elegible && l.partidos > 0) reparto.set(l.jugador, PUNTOS_RESTO);
  }
  return reparto;
}

export interface MesParaPuntos {
  mes: string;
  cerrado: boolean;
  tabla: LineaDelMes[];
}

export interface FilaPuntos {
  jugador: number;
  /** Suma de los meses cerrados dentro de la ventana. */
  total: number;
  /** Lo que sumaria si los meses abiertos cerraran hoy. */
  en_juego: number;
  /** Cuantos meses de la ventana le dieron puntos. */
  torneos: number;
  /** Puesto por `total`; null si todavia no sumo nada. Empates comparten puesto. */
  posicion: number | null;
}

export interface RankingPuntos {
  filas: FilaPuntos[];
  /** Los meses cerrados que cuentan, del mas viejo al mas nuevo. */
  meses_que_cuentan: string[];
  /** Los meses abiertos (normalmente uno; dos entre el fin de mes y el cierre de las 6). */
  meses_en_juego: string[];
}

/**
 * Arma el ranking. La ventana son los 12 meses calendario que terminan en el
 * ultimo mes cerrado: cuando cierra un mes, se cae el mismo mes del año anterior.
 */
export function rankingPorPuntos(meses: readonly MesParaPuntos[]): RankingPuntos {
  const cerrados = meses.filter((m) => m.cerrado).map((m) => m.mes).sort();
  const ultimo = cerrados[cerrados.length - 1];
  const desde = ultimo ? sumarMeses(ultimo, -(MESES_QUE_CUENTAN - 1)) : null;
  const cuentan = new Set(cerrados.filter((m) => desde !== null && m >= desde));
  const enJuego = meses.filter((m) => !m.cerrado).map((m) => m.mes).sort();

  const porJugador = new Map<number, FilaPuntos>();
  const fila = (id: number) => {
    let f = porJugador.get(id);
    if (!f) {
      f = { jugador: id, total: 0, en_juego: 0, torneos: 0, posicion: null };
      porJugador.set(id, f);
    }
    return f;
  };

  for (const m of meses) {
    if (!m.cerrado && !enJuego.includes(m.mes)) continue;
    if (m.cerrado && !cuentan.has(m.mes)) continue;
    for (const [id, pts] of puntosDelMes(m.tabla)) {
      const f = fila(id);
      if (m.cerrado) {
        f.total += pts;
        f.torneos += 1;
      } else {
        f.en_juego += pts;
      }
    }
  }

  const filas = ponerPuestos(
    [...porJugador.values()].sort(
      (a, b) => b.total - a.total || b.en_juego - a.en_juego || a.jugador - b.jugador,
    ),
  );
  return { filas, meses_que_cuentan: [...cuentan].sort(), meses_en_juego: enJuego };
}

/**
 * Numera por `total`, ya ordenadas: los empatados comparten puesto (1, 2, 2, 4)
 * y el que todavia no sumo nada queda sin puesto. Devuelve filas nuevas.
 */
export function ponerPuestos(filas: readonly FilaPuntos[]): FilaPuntos[] {
  const salida: FilaPuntos[] = [];
  filas.forEach((f, i) => {
    const anterior = salida[i - 1];
    const posicion =
      f.total === 0 ? null : anterior && anterior.total === f.total ? anterior.posicion : i + 1;
    salida.push({ ...f, posicion });
  });
  return salida;
}
